import { useState } from "react";
import { Trash2 } from "lucide-react";
import api, { formatApiError } from "../../../lib/api";

/**
 * Simple image uploader posting to /api/admin/uploads/image (backed by
 * local disk storage in `backend/car-images/`). Also accepts a pasted
 * URL so external hosting still works.
 *
 * v3.9.3 — display the full URL (prepend window.origin for relative
 * `/api/...` paths) so admins see a valid clickable link, but persist
 * the shorter relative form when we save (relative paths survive host
 * renames / preview vs prod).
 */
export function ImageUpload({ value, onChange, testId = "image-upload" }) {
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const displayValue = (() => {
    if (!value || value.startsWith("data:")) return "";
    if (/^https?:\/\//i.test(value)) return value;
    if (value.startsWith("/")) return origin + value;
    return value;
  })();
  const onUrlChange = (raw) => {
    if (!raw) { onChange(""); return; }
    if (origin && raw.startsWith(origin + "/")) {
      onChange(raw.slice(origin.length));
      return;
    }
    onChange(raw);
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("Image must be under 5 MB (currently " + Math.round(file.size / 1024) + " KB).");
      return;
    }
    setError("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex items-start gap-3">
        {value && (
          <div className="w-24 h-16 bg-white border border-black/10 dark:border-white/10 rounded flex items-center justify-center overflow-hidden">
            <img src={value} alt="preview" className="max-h-full max-w-full object-contain p-1" />
          </div>
        )}
        <div className="flex-1 space-y-2">
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            disabled={uploading}
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid={`${testId}-input`}
            className="block text-xs text-neutral-600 dark:text-neutral-300 file:mr-3 file:py-1.5 file:px-3 file:border-0 file:rounded file:bg-black file:text-white dark:file:bg-white dark:file:text-black file:cursor-pointer file:text-xs disabled:opacity-50"
          />
          <input
            type="text"
            placeholder="Or paste image URL / relative path"
            value={displayValue}
            onChange={(e) => onUrlChange(e.target.value)}
            className="w-full bg-transparent border-b border-black/15 dark:border-white/15 py-1 outline-none focus:border-black dark:focus:border-white text-xs"
            data-testid={`${testId}-url`}
          />
        </div>
      </div>
      {uploading && <p className="text-xs text-neutral-500">Uploading…</p>}
      {error && <p className="text-xs text-red-600 dark:text-rose-400" data-testid={`${testId}-error`}>{error}</p>}
      <p className="text-[10px] text-neutral-500 dark:text-neutral-400">
        PNG / JPEG / WebP up to 5 MB. Saved to /api/car-images/ or the cloud when available.
      </p>
    </div>
  );
}

/**
 * GalleryUpload — multi-image gallery editor (up to 2 extras beyond the
 * primary group image).
 */
export function GalleryUpload({ value = [], onChange, testId = "gallery-upload" }) {
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const MAX_GALLERY = 2;   // v3.9.3 — 1 primary + 2 gallery

  const addFiles = async (files) => {
    if (!files || files.length === 0) return;
    setError("");
    const remaining = Math.max(0, MAX_GALLERY - (value || []).length);
    if (remaining === 0) {
      setError(`Gallery is limited to ${MAX_GALLERY} images. Remove one first.`);
      return;
    }
    const slice = Array.from(files).slice(0, remaining);
    if (slice.length < files.length) {
      setError(`Only ${remaining} slot${remaining === 1 ? "" : "s"} left — extra images skipped.`);
    }
    setUploading(true);
    const uploaded = [];
    try {
      for (const file of slice) {
        if (!file.type.startsWith("image/")) continue;
        if (file.size > 5 * 1024 * 1024) {
          setError(`${file.name}: exceeds 5 MB — skipped.`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/uploads/image", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data.url);
      }
      if (uploaded.length > 0) onChange([...(value || []), ...uploaded]);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  const removeAt = (i) => {
    const next = [...(value || [])];
    next.splice(i, 1);
    onChange(next);
  };

  const move = (i, dir) => {
    const next = [...(value || [])];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div className="space-y-3" data-testid={testId}>
      {(value || []).length > 0 && (
        <div className="grid grid-cols-4 gap-3" data-testid={`${testId}-grid`}>
          {value.map((url, i) => (
            <div
              key={`${url}-${i}`}
              className="relative group border border-black/10 dark:border-white/10 rounded overflow-hidden"
              data-testid={`${testId}-item-${i}`}
            >
              <div className="bg-white h-24 flex items-center justify-center">
                <img
                  src={url}
                  alt={`gallery ${i + 1}`}
                  className="max-h-full max-w-full object-contain p-1"
                />
              </div>
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                <button
                  type="button"
                  onClick={() => move(i, -1)}
                  disabled={i === 0}
                  className="text-white text-xs px-2 py-1 bg-white/20 hover:bg-white/40 rounded disabled:opacity-30"
                  data-testid={`${testId}-move-up-${i}`}
                  aria-label="Move left"
                >←</button>
                <button
                  type="button"
                  onClick={() => move(i, 1)}
                  disabled={i === (value.length - 1)}
                  className="text-white text-xs px-2 py-1 bg-white/20 hover:bg-white/40 rounded disabled:opacity-30"
                  data-testid={`${testId}-move-down-${i}`}
                  aria-label="Move right"
                >→</button>
                <button
                  type="button"
                  onClick={() => removeAt(i)}
                  className="text-white text-xs px-2 py-1 bg-rose-600/80 hover:bg-rose-600 rounded"
                  data-testid={`${testId}-remove-${i}`}
                  aria-label="Remove image"
                ><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <input
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        disabled={uploading || (value || []).length >= MAX_GALLERY}
        onChange={(e) => addFiles(Array.from(e.target.files || []))}
        className="block text-xs text-neutral-600 dark:text-neutral-300 file:mr-3 file:py-1.5 file:px-3 file:border-0 file:rounded file:bg-black file:text-white dark:file:bg-white dark:file:text-black file:cursor-pointer file:text-xs disabled:opacity-50"
        data-testid={`${testId}-input`}
      />
      {uploading && <p className="text-xs text-neutral-500">Uploading gallery images…</p>}
      {error && <p className="text-xs text-red-600 dark:text-rose-400">{error}</p>}
      <p className="text-[10px] text-neutral-500 dark:text-neutral-400">
        Up to <strong>{MAX_GALLERY}</strong> extra images (5 MB each). Combined with the primary image above they form the customer image carousel. Hover a thumbnail to reorder or remove.
      </p>
    </div>
  );
}
